import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Send, Paperclip, X, Loader2, Instagram, Share2 } from "lucide-react";

const SERVICE_ICON = { instagram: Instagram };

export const SocialTab = ({ subscribers = [] }) => {
  const [configured, setConfigured] = useState(true);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [providerId, setProviderId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/social/channels")
      .then((r) => { setConfigured(r.data.configured); setChannels(r.data.channels || []); setSelected((r.data.channels || []).map((c) => c.id)); })
      .catch(() => setConfigured(false))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.url);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't upload image."); }
    finally { setUploading(false); }
  };

  const anyInstagram = channels.some((c) => selected.includes(c.id) && c.service === "instagram");

  const send = async () => {
    if (!text.trim()) { toast.error("Write something to post."); return; }
    if (!selected.length) { toast.error("Select at least one channel."); return; }
    if (anyInstagram && !image) { toast.error("Instagram posts need an image. Attach one to continue."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/social/post", { text, image_url: image, channel_ids: selected, provider_id: providerId || null });
      const failed = data.results.filter((r) => !r.ok);
      if (data.queued > 0) toast.success(`Queued ${data.queued} post${data.queued > 1 ? "s" : ""} to your Buffer queue.`);
      failed.forEach((r) => toast.error(`${r.service}: ${r.error}`));
      if (data.queued > 0) { setText(""); setImage(null); setProviderId(""); }
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't create the post."); }
    finally { setSending(false); }
  };

  if (loading) return <p className="text-muted-foreground">Loading Buffer…</p>;
  if (!configured) return (
    <div className="border border-dashed border-border rounded-sm p-8 text-center" data-testid="social-not-configured">
      <Share2 className="w-7 h-7 mx-auto text-muted-foreground" strokeWidth={1.5} />
      <p className="mt-2 font-medium">Buffer isn't connected</p>
      <p className="text-sm text-muted-foreground">Add a BUFFER_API_KEY to enable social posting.</p>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-5" data-testid="social-tab">
      <div>
        <h3 className="font-display text-xl font-medium">Compose a post</h3>
        <p className="text-sm text-muted-foreground">Posts are added to your Buffer queue for review before they publish. <b>#bookdhub</b> is added automatically.</p>
      </div>

      <div>
        <Label>Channels</Label>
        <div className="flex flex-wrap gap-3 mt-2">
          {channels.map((c) => {
            const Icon = SERVICE_ICON[c.service] || Share2;
            return (
              <label key={c.id} className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 cursor-pointer" data-testid={`social-channel-${c.id}`}>
                <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`social-channel-toggle-${c.id}`} />
                <Icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{c.displayName}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <Label htmlFor="social-text">Message</Label>
        <Textarea id="social-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder="What do you want to share?" className="mt-1.5 rounded-sm" data-testid="social-text-input" />
      </div>

      <div>
        <Label>Feature a provider's hashtag <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <select value={providerId} onChange={(e) => setProviderId(e.target.value)} data-testid="social-provider-select"
          className="mt-1.5 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm">
          <option value="">None</option>
          {subscribers.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">If the provider set a social hashtag, it'll be added to this post.</p>
      </div>

      <div>
        <Label>Image {anyInstagram && <span className="text-primary">(required for Instagram)</span>}</Label>
        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="social-image-input" />
        <div className="mt-1.5">
          {image ? (
            <div className="relative inline-block" data-testid="social-image-preview">
              <img src={mediaUrl(image)} alt="to post" className="h-28 w-auto rounded-sm border border-border" />
              <button type="button" onClick={() => setImage(null)} data-testid="social-image-remove"
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-secondary text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="social-attach-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}Attach image
            </Button>
          )}
        </div>
      </div>

      <Button onClick={send} disabled={sending || uploading} className="rounded-sm" data-testid="social-send-btn">
        {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}Add to Buffer queue
      </Button>
    </div>
  );
};
